import { useEffect } from "react";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Ganttv2, ViewMode as GanttViewMode } from "@/controls/ganttAsset";
import { BundleTooltip } from "./components/BundleTooltip";
import { GanttSkeleton } from "./components/GanttSkeleton";
import { useGanttData } from "./hooks";
import {
	GANTT_DELAYS_MENU_ID,
	useGanttOptionsContextMenu,
} from "../../hooks/useGanttOptionsContextMenu";
import { ViewMode } from "./models/types";
import {
	GANTT_WO_MENU_ID,
	useWorkOrderGanttMenu,
} from "@/pages/workOrders/hooks/useWorkOrderGanttMenu";

const getGanttViewMode = (startDate: Date, endDate: Date): GanttViewMode => {
	const hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
	if (hours <= 12) return GanttViewMode.Hour;
	if (hours <= 24) return GanttViewMode.TwoHour;
	if (hours <= 72) return GanttViewMode.HalfDay;
	if (hours <= 168) return GanttViewMode.Day;
	return GanttViewMode.Day;
};

interface GanttSectionProps {
	startDate: Date;
	endDate: Date;
	viewMode: ViewMode;
	containerWidth: number;
	onError?: (error: Error) => void;
	/** Home: one lane per weld line. Overview (default): a single line only. */
	multiLine?: boolean;
	/** Single-line mode target (null = Weld165). Ignored when multiLine. */
	assetId?: number | null;
}

export const GanttSection = ({
	startDate,
	endDate,
	viewMode,
	containerWidth,
	onError,
	multiLine = false,
	assetId = null,
}: GanttSectionProps) => {
	const ganttViewMode = getGanttViewMode(startDate, endDate);

	const { columnWidth, laneCount, assets, isLoading } = useGanttData({
		startDate,
		endDate,
		viewMode,
		ganttViewMode,
		containerWidth,
		onError,
		multiLine,
		assetId,
	});

	const rowHeight = multiLine ? 120 : 150;

	const { getOptions: getDelayMenuOptions, menu: delayMenu } =
		useGanttOptionsContextMenu(["ganttData"], "Home Gantt");

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getOptions: getWoMenuOptions, menu: woMenu } =
		useWorkOrderGanttMenu();

	useEffect(() => {
		registerConfig({
			id: GANTT_DELAYS_MENU_ID,
			getOptions: getDelayMenuOptions,
		});
		registerConfig({
			id: GANTT_WO_MENU_ID,
			getOptions: getWoMenuOptions,
		});
	}, []);

	const handleContextMenu = (event: React.MouseEvent, data: any) => {
		if (data?.DelayId) {
			showContextMenu(event, data, GANTT_DELAYS_MENU_ID);
			return;
		}
		// Work order band: WorkOrderId without an EventType
		if (data?.WorkOrderId != null && data?.EventType == null) {
			event.preventDefault();
			showContextMenu(event, data, GANTT_WO_MENU_ID);
		}
	};

	if (isLoading) {
		return <GanttSkeleton laneCount={laneCount} rowHeight={rowHeight} />;
	}

	return (
		<>
			<Ganttv2
				assets={assets}
				viewMode={ganttViewMode}
				startCalendarDate={startDate}
				endCalendarDate={endDate}
				columnWidth={columnWidth}
				rowHeight={rowHeight}
				barFill={45}
				lineSectionFill={20}
				lineFill={80}
				showArrowTriangle={false}
				TooltipContent={BundleTooltip}
				highlightHours={[{ hour: 0, color: " #75a5c6" }]}
				onContextMenu={handleContextMenu}
			/>
			{delayMenu}
			{woMenu}
		</>
	);
};
